import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import uuid from 'react-native-uuid';
import { COLOR, FONT, THEME } from '../../constants/styles';
import { CTAButton } from '../components';
import { Header, styles } from './common_ui';
import { loadAgentSession, sendAgentTurn, submitAgentForm } from './agent_slice';
import { setMode } from './service';

const buttonStyle = {
  borderWidth: 1,
  borderColor: COLOR.accent,
  borderRadius: 8,
  paddingHorizontal: 14,
  paddingVertical: 10,
  marginRight: 8,
  marginBottom: 8,
  backgroundColor: COLOR.button,
  color: COLOR.contrast,
  flexShrink: 1,
  maxWidth: '100%',
};

const buttonTextStyle = {
  flexShrink: 1,
  textAlign: 'center',
};

const ctaButtonStyle = {
  height: 'auto',
  minHeight: 55,
};

function AgentMessage({ message, content = message.content }) {
  const isUser = message.role === 'user';
  const displayedContent = isUser ? content : content?.split('\nOptions:')[0];
  return (
    <View
      style={{
        alignSelf: isUser ? 'flex-end' : 'flex-start',
        backgroundColor: isUser ? COLOR.callToAction : 'rgb(239,242,247)',
        borderRadius: 12,
        marginBottom: 12,
        maxWidth: '85%',
        padding: 14,
      }}
    >
      <Text style={[FONT.regular, { color: isUser ? 'white' : COLOR.text }]}>
        {displayedContent}
      </Text>
    </View>
  );
}

function PromptContext({ prompt }) {
  if (!prompt?.groupLabel && !prompt?.groupDescription && !prompt?.subjectLabel) return null;
  return (
    <View
      style={{
        borderLeftWidth: 4,
        borderLeftColor: COLOR.accent,
        marginBottom: 12,
        paddingHorizontal: 12,
        paddingVertical: 8,
      }}
    >
      {!!prompt.groupLabel && (
        <Text style={[FONT.semibold, { color: COLOR.text, marginBottom: 4 }]}>
          {prompt.groupLabel}
        </Text>
      )}
      {!!prompt.groupDescription && (
        <Text
          style={[FONT.regular, { color: COLOR.text, marginBottom: prompt.subjectLabel ? 8 : 0 }]}
        >
          {prompt.groupDescription}
        </Text>
      )}
      {!!prompt.subjectLabel && (
        <Text style={[FONT.semibold, { color: COLOR.text }]}>
          Family member: {prompt.subjectLabel}
        </Text>
      )}
    </View>
  );
}

function ReviewAnswers({ answers, onEdit }) {
  if (!answers?.length) return null;
  return (
    <View style={{ marginTop: 20 }}>
      <Text style={[FONT.semibold, { marginBottom: 10 }]}>Review accepted answers</Text>
      {answers.map((answer, index) => {
        const isNewContext = answer.contextKey !== answers[index - 1]?.contextKey;
        return (
          <React.Fragment key={`${answer.contextKey || 'default'}-${answer.questionId}`}>
            {isNewContext && <PromptContext prompt={answer} />}
            <View
              style={{
                borderBottomWidth: 1,
                borderBottomColor: 'rgb(224,224,224)',
                paddingVertical: 10,
              }}
            >
              <Text style={[FONT.regular, { color: COLOR.text }]}>{answer.label}</Text>
              <Text style={[FONT.semibold, { marginVertical: 5 }]}>{answer.answer}</Text>
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityLabel={`Edit ${answer.label}`}
                onPress={() => onEdit(answer.questionId)}
              >
                <Text style={[FONT.semibold, { color: COLOR.accent }]}>Edit</Text>
              </TouchableOpacity>
            </View>
          </React.Fragment>
        );
      })}
    </View>
  );
}

export default function PartAAgent({ token, onSwitchMode }) {
  const dispatch = useDispatch();
  const { height: windowHeight } = useWindowDimensions();
  const { session, isLoading, isSending, isSubmitting, isSubmitted, error, lastTurn } = useSelector(
    (state) => state.screens.partAAgent
  );
  const [input, setInput] = useState('');
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [textAlternative, setTextAlternative] = useState('');
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const prompt = session?.prompt;
  const promptId = prompt?.promptId;

  useEffect(() => {
    dispatch(loadAgentSession({ token }));
  }, [dispatch, token]);

  useEffect(() => {
    setSelectedOptions(prompt?.selectedOptions || []);
    setTextAlternative(prompt?.textAlternativeValue || '');
  }, [promptId]);

  useEffect(() => {
    scrollRef.current?.scrollToEnd?.({ animated: true });
  }, [session?.messages?.length, session?.status, isSending]);

  useEffect(() => {
    if (!isSending) inputRef.current?.focus();
  }, [promptId, isSending]);

  const progress = useMemo(() => {
    if (!session?.progress?.totalSections) return '';
    return `Section ${session.progress.section} of ${session.progress.totalSections}`;
  }, [session?.progress]);

  const sendTurn = (turn) => dispatch(sendAgentTurn({ token, turn }));

  const sendMessage = (message = input) => {
    const trimmedMessage = message?.trim();
    if (!trimmedMessage || isSending) return;
    setInput('');
    sendTurn({
      clientTurnId: uuid.v4(),
      kind: 'message',
      message: trimmedMessage,
    });
  };

  const editAnswer = (questionId) => {
    sendTurn({ clientTurnId: uuid.v4(), kind: 'edit_answer', questionId });
  };

  const chooseQuestion = (questionId) => {
    setSelectedOptions([]);
    sendTurn({ clientTurnId: uuid.v4(), kind: 'choose_question', questionId });
  };

  const sendGroupAnswer = () => {
    const trimmedTextAlternative = textAlternative.trim();
    if ((!selectedOptions.length && !trimmedTextAlternative) || isSending) return;
    sendTurn({
      clientTurnId: uuid.v4(),
      kind: 'answer_group',
      selectedOptions,
      ...(prompt.textAlternativeQuestionId ? { textAlternative: trimmedTextAlternative } : {}),
    });
  };

  const handleSwitchMode = async () => {
    await setMode(token, 'form');
    onSwitchMode('form');
  };

  const selfSchedule = () => {
    window.location.href = '/base/demo/pha';
  };

  const toggleOption = (option) => {
    setSelectedOptions((current) =>
      current.includes(option)
        ? current.filter((selected) => selected !== option)
        : [...current, option]
    );
  };

  const selectCheckOption = (option) => {
    setSelectedOptions((current) => (current.includes(option) ? [] : [option]));
  };

  if (isLoading && !session) {
    return (
      <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
        <ActivityIndicator />
        <Text style={[FONT.regular, { marginTop: 12 }]}>Loading your Part A conversation…</Text>
      </View>
    );
  }

  if (isSubmitted) {
    return (
      <View style={styles.container}>
        <View style={styles.innerContainer}>
          <Header label="Annual Periodic Health Assessment" />
          <View style={{ padding: 30, alignItems: 'center' }}>
            <Text style={[THEME.textSemibold, { textAlign: 'center', marginTop: 50 }]}>
              PHA Part A Submitted!
            </Text>
            <Text style={[FONT.regular, { textAlign: 'center', marginTop: 15 }]}>
              Thank you for submitting your PHA Part A.
            </Text>
            <CTAButton
              disabled={false}
              label="Self-Schedule Your PHA"
              onPress={selfSchedule}
              style={[ctaButtonStyle, { alignSelf: 'stretch', marginTop: 50 }]}
            />
          </View>
        </View>
      </View>
    );
  }

  const messages = session?.messages || [];
  const currentPromptMessage =
    session?.status === 'asking' && prompt?.options?.length
      ? [...messages].reverse().find((message) => message.role !== 'user')
      : null;
  return (
    <View style={styles.container}>
      <View style={styles.innerContainer}>
        <Header label="AI Guided PHA Part A" />
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingHorizontal: 20,
            paddingVertical: 10,
            borderBottomWidth: 1,
            borderBottomColor: 'rgb(224,224,224)',
          }}
        >
          <Text style={FONT.semibold}>{progress}</Text>
          <TouchableOpacity
            accessibilityRole="button"
            onPress={handleSwitchMode}
            style={buttonStyle}
          >
            <Text style={[FONT.semibold, buttonTextStyle, { color: COLOR.accent }]}>
              Use Graphical Guide
            </Text>
          </TouchableOpacity>
        </View>
        <ScrollView
          ref={scrollRef}
          style={{ flex: 1 }}
          contentContainerStyle={{ padding: 20 }}
          keyboardShouldPersistTaps="handled"
        >
          <View
            style={{
              backgroundColor: 'rgb(245,247,250)',
              borderRadius: 8,
              padding: 12,
              marginBottom: 16,
            }}
          >
            <Text style={FONT.regular}>
              AI helps interpret your response. Official form rules validate every accepted answer.
              You can switch to the graphical guide at any time.
            </Text>
          </View>
          {messages.map((message) => (
            <AgentMessage
              key={message.messageId}
              content={
                message === currentPromptMessage && prompt.text ? prompt.text : message.content
              }
              message={message}
            />
          ))}
          {!!error && (
            <View
              accessibilityRole="alert"
              style={{ backgroundColor: 'rgb(255,235,235)', borderRadius: 8, padding: 12 }}
            >
              <Text style={[FONT.regular, { color: COLOR.error || 'darkred' }]}>
                {error.message || 'The AI agent is temporarily unavailable.'}
              </Text>
              {!!lastTurn && (
                <TouchableOpacity
                  accessibilityRole="button"
                  onPress={() => sendTurn(lastTurn)}
                  style={[buttonStyle, { marginTop: 10, alignSelf: 'flex-start' }]}
                >
                  <Text style={[FONT.semibold, buttonTextStyle, { color: COLOR.accent }]}>
                    Retry
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          {session?.status === 'section_review' && (
            <>
              <ReviewAnswers answers={session.answers} onEdit={editAnswer} />
              <CTAButton
                disabled={isSending}
                fontStyle={buttonTextStyle}
                label="These answers are correct — Continue"
                onPress={() => sendTurn({ clientTurnId: uuid.v4(), kind: 'confirm_section' })}
                style={[ctaButtonStyle, { marginTop: 20 }]}
              />
            </>
          )}

          {session?.status === 'final_review' && (
            <>
              <ReviewAnswers answers={session.answers} onEdit={editAnswer} />
              <CTAButton
                disabled={isSubmitting || !session.validation?.valid}
                fontStyle={buttonTextStyle}
                label="Submit PHA Part A"
                onPress={() => dispatch(submitAgentForm({ token }))}
                style={[ctaButtonStyle, { marginTop: 20, marginBottom: 20 }]}
              />
            </>
          )}

          {!!session?.completedRepeatableParentId && (
            <TouchableOpacity
              accessibilityRole="button"
              onPress={() =>
                sendTurn({
                  clientTurnId: uuid.v4(),
                  kind: 'add_repeated',
                  questionId: session.completedRepeatableParentId,
                })
              }
              style={[buttonStyle, { alignSelf: 'flex-start' }]}
            >
              <Text style={[FONT.semibold, buttonTextStyle, { color: COLOR.accent }]}>
                Add another entry
              </Text>
            </TouchableOpacity>
          )}
        </ScrollView>

        {session?.status === 'asking' && prompt && (
          <View
            style={{
              borderTopWidth: 1,
              borderTopColor: 'rgb(224,224,224)',
              padding: 16,
              backgroundColor: 'white',
            }}
          >
            <PromptContext prompt={prompt} />
            {prompt.type === 'multi_select' ? (
              <>
                <ScrollView
                  contentContainerStyle={{ flexDirection: 'row', flexWrap: 'wrap' }}
                  keyboardShouldPersistTaps="handled"
                  nestedScrollEnabled
                  style={{ flexGrow: 0, maxHeight: windowHeight / 2 }}
                >
                  {prompt.options.map((option) => (
                    <TouchableOpacity
                      key={option}
                      accessibilityRole="checkbox"
                      accessibilityState={{
                        checked: selectedOptions.includes(option),
                        selected: selectedOptions.includes(option),
                      }}
                      disabled={isSending}
                      onPress={() => toggleOption(option)}
                      style={[
                        buttonStyle,
                        selectedOptions.includes(option) ? { backgroundColor: COLOR.accent } : null,
                      ]}
                    >
                      <Text
                        style={[
                          FONT.regular,
                          buttonTextStyle,
                          { color: selectedOptions.includes(option) ? COLOR.text : COLOR.contrast },
                        ]}
                      >
                        {selectedOptions.includes(option) ? '✓ ' : ''}
                        {option}
                      </Text>
                    </TouchableOpacity>
                  ))}
                  {!!prompt.textAlternativeQuestionId && !prompt.subjectLabel && (
                    <TouchableOpacity
                      accessibilityRole="button"
                      disabled={isSending}
                      onPress={() => chooseQuestion(prompt.textAlternativeQuestionId)}
                      style={buttonStyle}
                    >
                      <Text style={[FONT.regular, buttonTextStyle, { color: COLOR.contrast }]}>
                        {prompt.textAlternativeLabel}
                      </Text>
                    </TouchableOpacity>
                  )}
                </ScrollView>
                {!!prompt.textAlternativeQuestionId && !!prompt.subjectLabel && (
                  <View style={{ marginBottom: 12 }}>
                    <Text style={[FONT.semibold, { color: COLOR.text, marginBottom: 6 }]}>
                      {prompt.textAlternativeLabel}
                    </Text>
                    <TextInput
                      accessibilityLabel={`${prompt.textAlternativeLabel} for ${prompt.subjectLabel}`}
                      editable={!isSending}
                      maxLength={1000}
                      multiline
                      onChangeText={setTextAlternative}
                      placeholder="Type another condition"
                      style={{
                        minHeight: 44,
                        maxHeight: 120,
                        borderWidth: 1,
                        borderColor: 'rgb(190,190,190)',
                        borderRadius: 8,
                        padding: 10,
                      }}
                      value={textAlternative}
                    />
                  </View>
                )}
                <CTAButton
                  disabled={
                    (!selectedOptions.length &&
                      (!prompt.subjectLabel || !textAlternative.trim())) ||
                    isSending
                  }
                  fontStyle={buttonTextStyle}
                  label={prompt.subjectLabel ? 'Save conditions' : 'Send selections'}
                  onPress={
                    prompt.subjectLabel
                      ? sendGroupAnswer
                      : () => sendMessage(selectedOptions.join(', '))
                  }
                  style={ctaButtonStyle}
                />
              </>
            ) : prompt.answerType === 'check' && prompt.options?.length ? (
              <>
                <ScrollView
                  contentContainerStyle={{ flexDirection: 'row', flexWrap: 'wrap' }}
                  keyboardShouldPersistTaps="handled"
                  nestedScrollEnabled
                  style={{ flexGrow: 0, maxHeight: windowHeight / 2 }}
                >
                  {prompt.options.map((option) => {
                    const isSelected = selectedOptions.includes(option);
                    return (
                      <TouchableOpacity
                        key={option}
                        accessibilityRole={prompt.options.length === 1 ? 'checkbox' : 'radio'}
                        accessibilityState={{ checked: isSelected, selected: isSelected }}
                        disabled={isSending}
                        onPress={() => selectCheckOption(option)}
                        style={[buttonStyle, isSelected ? { backgroundColor: COLOR.accent } : null]}
                      >
                        <Text
                          style={[
                            FONT.regular,
                            buttonTextStyle,
                            { color: isSelected ? COLOR.text : COLOR.contrast },
                          ]}
                        >
                          {isSelected ? '✓ ' : ''}
                          {option}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                  {!!prompt.textAlternativeQuestionId && (
                    <TouchableOpacity
                      accessibilityRole="button"
                      disabled={isSending}
                      onPress={() => chooseQuestion(prompt.textAlternativeQuestionId)}
                      style={buttonStyle}
                    >
                      <Text style={[FONT.regular, buttonTextStyle, { color: COLOR.contrast }]}>
                        {prompt.textAlternativeLabel}
                      </Text>
                    </TouchableOpacity>
                  )}
                </ScrollView>
                <CTAButton
                  disabled={!selectedOptions.length || isSending}
                  fontStyle={buttonTextStyle}
                  label="Send selection"
                  onPress={() => sendMessage(selectedOptions[0])}
                  style={ctaButtonStyle}
                />
              </>
            ) : prompt.options?.length ? (
              <ScrollView
                contentContainerStyle={{ flexDirection: 'row', flexWrap: 'wrap' }}
                keyboardShouldPersistTaps="handled"
                nestedScrollEnabled
                style={{ flexGrow: 0, maxHeight: windowHeight / 2 }}
              >
                {prompt.options.map((option) => (
                  <TouchableOpacity
                    key={option}
                    accessibilityRole="button"
                    disabled={isSending}
                    onPress={() => sendMessage(option)}
                    style={buttonStyle}
                  >
                    <Text style={[FONT.regular, buttonTextStyle, { color: COLOR.contrast }]}>
                      {option}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            ) : (
              <>
                <View style={{ flexDirection: 'row', alignItems: 'flex-end' }}>
                  <TextInput
                    ref={inputRef}
                    accessibilityLabel={`Answer ${prompt.text}`}
                    autoFocus
                    blurOnSubmit
                    editable={!isSending}
                    multiline
                    onChangeText={setInput}
                    onSubmitEditing={() => sendMessage()}
                    placeholder={
                      prompt.type === 'date'
                        ? 'YYYY-MM-DD or describe the date'
                        : 'Type your answer'
                    }
                    returnKeyType="send"
                    style={{
                      flex: 1,
                      minHeight: 44,
                      maxHeight: 120,
                      borderWidth: 1,
                      borderColor: 'rgb(190,190,190)',
                      borderRadius: 8,
                      padding: 10,
                      marginRight: 10,
                    }}
                    value={input}
                  />
                  <TouchableOpacity
                    accessibilityRole="button"
                    disabled={!input.trim() || isSending}
                    onPress={() => sendMessage()}
                    style={[buttonStyle, { marginBottom: 0 }]}
                  >
                    <Text style={[FONT.semibold, buttonTextStyle, { color: COLOR.accent }]}>
                      Send
                    </Text>
                  </TouchableOpacity>
                </View>
                {prompt.required === false && (
                  <TouchableOpacity
                    accessibilityRole="button"
                    disabled={isSending}
                    onPress={() =>
                      sendTurn({
                        clientTurnId: uuid.v4(),
                        kind: 'skip_question',
                      })
                    }
                    style={[buttonStyle, { alignSelf: 'flex-start', marginTop: 8 }]}
                  >
                    <Text style={[FONT.semibold, buttonTextStyle, { color: COLOR.accent }]}>
                      Skip this question
                    </Text>
                  </TouchableOpacity>
                )}
              </>
            )}
            {isSending && <ActivityIndicator style={{ marginTop: 8 }} />}
          </View>
        )}
      </View>
    </View>
  );
}
